import { planResourceGenerator, type ResourceFamily, type ResourceInputs, type ResourceCommand } from '../janus-engine/inventory/resource-generator';
import { browserRollSource } from '../janus-engine/actions/roll-source';
import { loadSpawnEncounter } from '../shared/spawn-browser-bridge';
import { refuseUnported } from '../janus-engine/unported';
import { generatorDestination } from '../janus-engine/inventory/generator-destination';
import { prepareGeneratorDelivery, GENERATOR_SAVE_PATH } from '../shared/generator-delivery';
import { truthFetch } from '../shared/truth-sink';

const pending = new Map<string, { command: ResourceCommand; deliver: ReturnType<typeof prepareGeneratorDelivery> }>();
const running = new Set<string>();
const value = (root: Element, id: string): unknown => {
    const element = root.querySelector<HTMLInputElement | HTMLSelectElement>(`[id="${id}"]`)
        ?? document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    return element?.type === 'checkbox' ? (element as HTMLInputElement).checked ? element.value : 0 : element?.value ?? '';
};

/** Four resource controls only. Transport retries replay the complete saved command, including its UUIDs. */
export async function generateResource(family: ResourceFamily, button: HTMLButtonElement): Promise<void> {
    const root = button.closest('#div_encounter_random_generators') ?? document.documentElement;
    const destination = generatorDestination(root);
    const key = `${destination}:${family}`;
    if (running.has(key)) return;
    running.add(key); button.disabled = true;
    const warning = document.getElementById('div_sidelog_warning');
    try {
        if (!destination) throw new Error('resource-generator:destination-required');
        let prepared = pending.get(key);
        if (!prepared) {
            const values = Object.fromEntries(['random_generator_dice', 'random_generator_die', 'random_generator_mod',
                'random_generator_scatter', 'random_generator_constrain_to_environment_type', 'random_generator_cost_gp_min',
                'random_generator_cost_gp_max', `random_${family}`].map(id => [id, value(root, id)]));
            const [response, encounter] = await Promise.all([
                fetch(BASE_URL + 'options/api/resource_generator_inputs_inc.php?destination_id=' + encodeURIComponent(destination), { credentials: 'same-origin' }),
                loadSpawnEncounter(destination),
            ]);
            const input = await response.json() as ResourceInputs & { error?: string };
            if (!response.ok || input.schema !== 'resource-generator-inputs.v1') throw new Error(input.error ?? 'resource-generator:inputs-unavailable');
            const roll = browserRollSource();
            const command = planResourceGenerator(family, values, input, encounter, destination,
                { uuid: () => crypto.randomUUID().toUpperCase(), die: sides => roll.d(sides) });
            if (!command.rows.length) { if (warning) warning.textContent = command.summary; return; }
            prepared = { command, deliver: prepareGeneratorDelivery(command, input, encounter) };
            pending.set(key, prepared);
        }
        const { command } = prepared;
        const response = await truthFetch(BASE_URL + GENERATOR_SAVE_PATH, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command),
        });
        const result = await response.json() as { ok?: boolean; error?: string; created_ids?: string[] };
        if (!response.ok || !result.ok) throw new Error(result.error ?? `resource-generator:save-http:${response.status}`);
        await prepared.deliver(response);
        pending.delete(key);
        if (warning) warning.textContent = command.summary;
        if (result.created_ids?.length) {
            window.dispatchEvent(new CustomEvent('ft:random-generator-saved', { detail: { destination, ids: result.created_ids, family } }));
            try { await (window as Window & { ftRefreshEncounterXZAfterInventoryChange?: () => Promise<unknown> }).ftRefreshEncounterXZAfterInventoryChange?.(); }
            catch { if (warning) warning.textContent = `${command.summary} Reopen inventory to see the saved result.`; }
        }
    } catch (error) {
        const reason = error instanceof Error ? error.message : 'resource-generator:planning-failed';
        if (warning) warning.textContent = `Generation failed — retry: ${reason}`;
        refuseUnported(reason);
    } finally { running.delete(key); button.disabled = false; }
}
declare global { interface Window { ftGenerateResource: typeof generateResource } }
window.ftGenerateResource = generateResource;
